// l2b-test-exercise.js — Language-to-Body testing screen.
//
// Adapted from KFE Web's <kfe-l2b-test-exercise> (Brief 003). The L2BTestEngine
// wiring, tile UI (the same <kfe-l2b-drag-area>), feedback, and pass/fail
// summary are kept the same; the swaps are the data plumbing (demo
// content-provider + learner-store + the `audio` singleton), inlined English UI
// strings, an inline summary view (KFE's <kfe-completion-screen> isn't ported),
// and a trimmed pool rule.
//
// Pool (Brief 003 success criterion 3): mastered MMUs only. The learner store's
// mastered set ∩ the Testing exercise's MMUs forms the pool — no global top-up
// (KFE topped up from the full L2B registry; the demo deliberately tests only
// what the learner has actually mastered). Need ≥4 to form a tile grid; below
// that we show a "train first" empty state.
//
// Testing is assessment-only here: it does NOT record attempts back to the
// store, so taking the test never changes mastery.
//
// Reactive-prop landmine (Brief 003 §6): `itemId` via `static props` only, read
// with a cast — never an instance field.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { audio } from "../core/audio-engine.js";
import * as lang from "../i18n/language-manager.js";
import { L2BTestEngine } from "../engines/l2b-test-engine.js";
import {
  applyL2bDefensiveFilters,
  mmusToItemData,
} from "../engines/l2b-engine.js";
import { l2bModeRenderFlags } from "../data/mmu-loader.js";
import { loadContent, getExercise } from "../data/demo-content-provider.js";
import * as learner from "../data/learner-store.js";

const FEEDBACK_MS = 900;
const DEFAULT_NUM_INSTANCES = 8;
const DEFAULT_PASS_SCORE = 70;
const MASTERY_TARGET = 3;
const MIN_POOL = 4;

/** @param {string|null} l2bType → L2BTestConfig.modality */
function modalityFor(l2bType) {
  if (l2bType === "Text only") return "textOnly";
  if (l2bType === "Audio and Text") return "audioAndText";
  return "audioOnly";
}

export class L2BTestExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: min(720px, 100vw - 32px); margin-inline: auto; }
    h2 { margin-top: 0; color: var(--l2b-fg); text-shadow: 0 1px 3px rgba(0,0,0,0.6); }
    /* Target prompt framed in the dojo panel sprite. */
    .target { margin: 1rem 0; padding: 0.4rem 1rem; display: flex;
              align-items: center; gap: 1rem;
              border: var(--l2b-panel-border) solid transparent;
              border-image: var(--l2b-skin-panel) var(--l2b-panel-slice) / var(--l2b-panel-border) stretch;
              background: var(--l2b-surface); border-radius: var(--l2b-radius);
              filter: drop-shadow(0 4px 10px rgba(0,0,0,0.45)); }
    .target .word { font-size: var(--l2b-h2); flex: 1; color: var(--l2b-fg);
                    font-weight: 700; }
    .grid { margin-top: 1rem; }
    /* Pass/fail summary + empty state framed as dojo panels. */
    .summary, .empty { padding: 1.5rem 2rem; text-align: center; margin: 1rem auto;
                max-width: 32rem;
                border: var(--l2b-panel-border) solid transparent;
                border-image: var(--l2b-skin-panel) var(--l2b-panel-slice) / var(--l2b-panel-border) stretch;
                background: var(--l2b-surface); border-radius: var(--l2b-radius);
                filter: drop-shadow(0 6px 18px rgba(0,0,0,0.5)); }
    .summary .verdict { font-size: var(--l2b-h1); margin-bottom: 0.5rem; font-weight: 800; }
    .summary .verdict.pass { color: var(--l2b-success); }
    .summary .verdict.fail { color: var(--l2b-error); }
    .summary .score { font-size: var(--l2b-h2); margin-bottom: 0.75rem; color: var(--l2b-fg); }
    .summary .threshold { color: var(--l2b-muted); margin-bottom: 1.25rem; }
    .summary .actions { display: flex; flex-direction: column; gap: 0.75rem;
                        align-items: center; }
    .empty { color: var(--l2b-muted); }
    .empty h2 { color: var(--l2b-fg); text-shadow: none; }
    /* Primary action = dojo button sprite (cream centre → dark ink). */
    button.primary { padding: 0.7rem 1.5rem; color: #4a2f1a; font-weight: 800;
                     background: transparent; cursor: pointer;
                     border: var(--l2b-btn-border) solid transparent;
                     border-image: var(--l2b-skin-btn) var(--l2b-btn-slice) / var(--l2b-btn-border) stretch;
                     filter: drop-shadow(0 3px 6px rgba(0,0,0,0.45));
                     transition: transform 0.1s ease, filter 0.1s ease; }
    button.primary:hover { filter: drop-shadow(0 5px 10px rgba(0,0,0,0.5)) brightness(1.04); }
    button.primary:active { transform: translateY(1px); }
    button.replay { padding: 0.5rem 1rem 0.5rem 0.85rem; border-radius: 999px;
                    border: 1px solid var(--l2b-border); background: rgba(0,0,0,0.3);
                    color: var(--l2b-fg); cursor: pointer; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.4rem; }
    button.replay::before { content: ""; width: 1.1em; height: 1.1em; flex: none;
                    background: var(--l2b-skin-volume) center / contain no-repeat; }
    button.replay:hover { background: rgba(0,0,0,0.45); }
    .start-prompt { padding: 2rem; text-align: center; }
    .progress { font-size: 0.9rem; color: var(--l2b-fg); font-weight: 600;
                text-align: right; margin-bottom: 0.25rem;
                text-shadow: 0 1px 2px rgba(0,0,0,0.6); }
    /* Feedback — glyph hidden, dojo tick/fail sprite via ::before (render() intact). */
    .feedback { padding: 0.75rem; border-radius: 12px; margin: 0.75rem 0;
                font-size: 0; text-align: center; line-height: 1;
                display: flex; align-items: center; justify-content: center; }
    .feedback::before { content: ""; width: 2.5rem; height: 2.5rem;
                background-position: center; background-repeat: no-repeat;
                background-size: contain; }
    .feedback.correct { background: rgba(31, 61, 31, 0.85); border: 2px solid var(--l2b-success); }
    .feedback.correct::before { background-image: var(--l2b-skin-tick); }
    .feedback.wrong   { background: rgba(61, 31, 31, 0.85); border: 2px solid var(--l2b-error); }
    .feedback.wrong::before { background-image: var(--l2b-skin-fail); }
    a.cta { display: inline-block; text-decoration: none;
            background: rgba(0,0,0,0.3); color: var(--l2b-fg);
            border: 1px solid var(--l2b-border);
            padding: 0.6rem 1.4rem; border-radius: var(--l2b-radius); font-weight: 700; }
    a.cta:hover { background: rgba(0,0,0,0.45); }
    /* Brief 013 — mobile/short-height compaction (mirrors l2b-exercise.js). The
       shared <kfe-l2b-drag-area> already compacts the drop zone; this trims the
       Testing screen's own vertical rhythm so its tileGrid fits one screen too.
       Guarded behind the same short-height/narrow-width query → desktop untouched. */
    @media (max-height: 720px), (max-width: 600px) {
      h2 { font-size: 1.1rem; }
      .target { margin: 0.5rem 0; }
      .progress { margin-bottom: 0.15rem; }
      .feedback { margin: 0.4rem 0; padding: 0.4rem; }
      .grid { margin-top: 0.5rem; }
    }
  `;

  /** @type {L2BTestEngine | null} */
  engine = null;

  constructor() {
    super();
    this._started = false;
    this._notEnoughMastered = false;
    /** @type {string|null} */ this._lastAudioConceptId = null;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._feedbackTimer = null;
    this._numberOfInstances = DEFAULT_NUM_INSTANCES;
    this._passScore = DEFAULT_PASS_SCORE;
    // Brief 006e: audio-only presentation — hide the target word text (mirrors
    // l2b-exercise.js). Demo L2BType pinned to "Audio only" →
    // { showText: false, autoPlay: true }.
    /** @type {string|null} */ this._l2bType = "Audio only";
    this._renderFlags = { showText: false, autoPlay: true };
    /** @type {(() => void) | null} */ this._langOff = null;
  }

  async onConnected() {
    await loadContent();
    // Re-render labels when the L1 changes (Gotcha 4 — guarded + addCleanup so
    // "Test again" re-entry never stacks handlers).
    if (!this._langOff) {
      this._langOff = lang.onLanguageChanged(() => this._scheduleRender());
      this.addCleanup(() => { this._langOff?.(); this._langOff = null; });
    }
    const itemId = Number(/** @type {any} */ (this).itemId);
    const ex = Number.isFinite(itemId) ? getExercise(itemId) : null;
    if (!ex) {
      this._notEnoughMastered = true;
      this._scheduleRender();
      return;
    }

    this._l2bType = "Audio only";
    this._renderFlags = l2bModeRenderFlags(this._l2bType);

    // Pool = mastered MMUs ∩ this Testing exercise's MMUs. Preserves the
    // exercise's order (the engine shuffles anyway, but it's a clear audit trail).
    const masteredIds = new Set(learner.getMasteredMmuIds(MASTERY_TARGET));
    const pool = applyL2bDefensiveFilters(ex.mmus).filter((m) => masteredIds.has(m.mmuId));

    if (pool.length < MIN_POOL) {
      this._notEnoughMastered = true;
      this._scheduleRender();
      return;
    }

    const itemData = mmusToItemData(pool);
    this.engine = new L2BTestEngine(itemData, {
      numberOfInstances: this._numberOfInstances,
      passScore: this._passScore,
      modality: modalityFor(this._l2bType),
    });

    const onState = () => this._scheduleRender();
    this.engine.addEventListener("stateChanged", onState);
    // addCleanup every engine listener so re-entry doesn't stack handlers/audio.
    this.addCleanup(() => this.engine?.removeEventListener("stateChanged", onState));

    this._scheduleRender();
  }

  onDisconnected() {
    if (this._feedbackTimer) clearTimeout(this._feedbackTimer);
    audio.stop();
  }

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    const state = this.engine.start();
    this._afterStateChange(state);
  }

  _handleReplay() {
    if (!this.engine) return;
    const state = this.engine.snapshot();
    const target = state.targetItem;
    if (target) this._playAudioForTarget(target, /*force*/ true);
  }

  /** @param {string} conceptId */
  _handlePick(conceptId) {
    if (!this.engine) return;
    if (this._feedbackTimer) return;
    const fb = this.engine.onTileSelected(conceptId);
    this._afterStateChange(fb);
    this._feedbackTimer = setTimeout(() => {
      this._feedbackTimer = null;
      const next = this.engine?.onFeedbackComplete();
      if (next) this._afterStateChange(next);
    }, FEEDBACK_MS);
  }

  _onTestAgain() {
    if (this._feedbackTimer) { clearTimeout(this._feedbackTimer); this._feedbackTimer = null; }
    audio.stop();
    this._started = false;
    this._notEnoughMastered = false;
    this._lastAudioConceptId = null;
    this.engine = null;
    this._scheduleRender();
    this.onConnected();
  }

  /** @param {import("../engines/l2b-test-engine.js").L2BTestState} state */
  _afterStateChange(state) {
    if (state.mode === "tileGrid" && state.targetItem && this._renderFlags.autoPlay) {
      this._playAudioForTarget(state.targetItem, false);
    }
  }

  /**
   * @param {import("../engines/l2b-engine.js").L2BItemData} target
   * @param {boolean} force
   */
  _playAudioForTarget(target, force) {
    if (!force && this._lastAudioConceptId === target.conceptId) return;
    this._lastAudioConceptId = target.conceptId;
    // Target (L2) phrase audio via the language manager (Brief 004).
    audio.play({ url: lang.targetAudioUrl(target.mmu) }).catch((e) => {
      console.warn("[l2b-test] audio play failed for", target.conceptId, e);
    });
  }

  render() {
    if (this._notEnoughMastered) {
      return html`<div class="empty">
        <h2>${t("l2bTest.heading")}</h2>
        <p>${t("l2bTest.trainFirst", { n: MIN_POOL })}</p>
        <p><a class="cta" href="/dashboard">${t("nav.backToDashboard")}</a></p>
      </div>`;
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("l2b.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("l2bTest.heading")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();

    if (s.mode === "complete") {
      const verdict = s.passed ? "pass" : "fail";
      const verdictText = s.passed ? t("l2bTest.passed") : t("l2bTest.keepPractising");
      const percent = Math.round(s.accuracyPercent);

      const again = document.createElement("button");
      again.type = "button";
      again.className = "primary";
      again.textContent = t("l2bTest.testAgain");
      again.addEventListener("click", () => this._onTestAgain());

      return html`
        <section class="summary">
          <div class="verdict ${verdict}">${verdictText}</div>
          <div class="score">${t("l2bTest.score", { correct: s.correctCount, total: s.totalRounds, percent })}</div>
          <div class="threshold">${t("l2bTest.threshold", { percent: Math.round(s.passScore) })}</div>
          <div class="actions">
            ${again}
            <a class="cta" href="/dashboard">${t("nav.backToDashboard")}</a>
          </div>
        </section>
      `;
    }

    const target = s.targetItem;
    if (!target) return html`<p>${t("loading")}</p>`;

    const dragArea = document.createElement("kfe-l2b-drag-area");
    /** @type {any} */ (dragArea).items = s.tiles.map((d) => d.mmu);
    /** @type {any} */ (dragArea).disabled = s.mode === "feedback";
    dragArea.addEventListener("pick", (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      this._handlePick(String(detail.mmuId));
    });

    const replay = document.createElement("button");
    replay.type = "button";
    replay.className = "replay";
    replay.textContent = `🔊 ${t("audio.replay")}`;
    replay.addEventListener("click", () => this._handleReplay());

    const fbBlock = s.mode === "feedback"
      ? html`<div class="feedback ${s.wasCorrect ? "correct" : "wrong"}">
          ${s.wasCorrect ? "✓" : "✗"}
        </div>`
      : html``;

    const current = Math.min(s.currentRound + 1, s.totalRounds);

    return html`
      <div class="progress">${current} / ${s.totalRounds}</div>
      <h2>${t("l2bTest.heading")}</h2>
      <div class="target">
        ${this._renderFlags.showText
          ? html`<span class="word">${lang.targetText(target.mmu)}</span>`
          : html`<span class="word" aria-hidden="true"></span>`}
        ${replay}
      </div>
      ${fbBlock}
      <div class="grid">${dragArea}</div>
    `;
  }
}
